/**
 * PageTransition.jsx
 * Shows an "AI generating" overlay animation ONLY when the user's auth state
 * changes — i.e. logging in (logged out → logged in) or logging out. It no
 * longer fires on regular link/route navigation, which was adding ~1.4s of
 * overlay to every new page.
 */
import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";

const ANIM_DURATION = 1400; // ms before overlay fades out

/* ── Animated dots ── */
function Dots() {
  const [count, setCount] = useState(1);
  useEffect(() => {
    const id = setInterval(() => setCount((c) => (c % 3) + 1), 380);
    return () => clearInterval(id);
  }, []);
  return <span>{".".repeat(count)}</span>;
}

/* ── Shimmer scan bar ── */
const shimmerStyle = `
@keyframes s180-scan {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(400%); }
}
@keyframes s180-pulse-glow {
  0%, 100% { opacity: 0.6; }
  50%       { opacity: 1; }
}
@keyframes s180-fade-out {
  0%   { opacity: 1; }
  70%  { opacity: 1; }
  100% { opacity: 0; }
}
@keyframes s180-particle {
  0%   { transform: translateY(0) scale(1);   opacity: 0.8; }
  100% { transform: translateY(-60px) scale(0); opacity: 0; }
}
`;

export default function PageTransition() {
  const { currentUser } = useAuth();
  const isLoggedIn = !!currentUser;

  const [visible, setVisible] = useState(false);
  const [label, setLabel] = useState("Loading");
  const timerRef = useRef(null);
  // Initialized to the mount-time auth state (auth is already resolved by the
  // time this renders), so the initial load never animates — only real changes.
  const prevLoggedIn = useRef(isLoggedIn);

  useEffect(() => {
    if (prevLoggedIn.current === isLoggedIn) return;
    const justLoggedIn = !prevLoggedIn.current && isLoggedIn;
    prevLoggedIn.current = isLoggedIn;

    setLabel(justLoggedIn ? "Your Workspace" : "TheScope180");
    setVisible(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setVisible(false), ANIM_DURATION);

    return () => clearTimeout(timerRef.current);
  }, [isLoggedIn]);

  if (!visible) return null;

  return (
    <>
      <style>{shimmerStyle}</style>
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          pointerEvents: "none",
          animation: `s180-fade-out ${ANIM_DURATION}ms ease forwards`,
          background: "rgba(8, 14, 30, 0.82)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
        }}
      >
        {/* Scan line */}
        <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "25%",
            height: "100%",
            background: "linear-gradient(90deg, transparent, rgba(99,102,241,0.12), rgba(99,102,241,0.22), rgba(99,102,241,0.12), transparent)",
            animation: `s180-scan ${ANIM_DURATION * 0.7}ms ease-in-out forwards`,
          }} />
        </div>

        {/* Central card */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 14,
          padding: "28px 40px",
          borderRadius: 24,
          border: "1px solid rgba(99,102,241,0.3)",
          background: "rgba(15,23,42,0.9)",
          boxShadow: "0 0 60px rgba(99,102,241,0.2)",
          backdropFilter: "blur(12px)",
          animation: `s180-pulse-glow ${700}ms ease-in-out infinite`,
        }}>
          {/* AI icon */}
          <div style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #6366F1, #8B5CF6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 0 24px rgba(99,102,241,0.5)",
            fontSize: 22,
          }}>
            ✦
          </div>

          {/* Streaming text */}
          <div style={{
            fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
            color: "#e2e8f0",
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "0.04em",
            textAlign: "center",
          }}>
            <span style={{ color: "rgba(148,163,184,0.6)", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.12em", display: "block", marginBottom: 4 }}>
              TheScope180 AI
            </span>
            Generating {label}<Dots />
          </div>

          {/* Progress bar */}
          <div style={{
            width: 200,
            height: 3,
            borderRadius: 99,
            background: "rgba(99,102,241,0.2)",
            overflow: "hidden",
          }}>
            <div style={{
              height: "100%",
              borderRadius: 99,
              background: "linear-gradient(90deg, #6366F1, #8B5CF6, #A78BFA)",
              animation: `s180-scan ${ANIM_DURATION * 0.9}ms ease-in-out forwards`,
              width: "40%",
            }} />
          </div>
        </div>

        {/* Floating particles */}
        {[...Array(5)].map((_, i) => (
          <div key={i} style={{
            position: "absolute",
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: `rgba(${[99, 139, 167, 99, 139][i]}, ${[102, 92, 183, 102, 92][i]}, 241, 0.7)`,
            left: `${20 + i * 15}%`,
            top: `${40 + (i % 3) * 10}%`,
            animation: `s180-particle ${600 + i * 120}ms ease-out ${i * 100}ms forwards`,
          }} />
        ))}
      </div>
    </>
  );
}
